import { UserDto } from '@act/shared/data-transfer-objects';
import { useEffect, useState } from 'react';
import useTransferRequestDialog from 'react-components/dialog-forms/hooks/use-transfer-request-dialog';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { useAppSelector } from 'redux/store-hooks';
import { useAppNavigation } from '../app/routing/routes';
import { PaginatedTable, PaginationOrSortParameters } from '@act/storybook';
import { MEMBER_TABLE_COLUMN_CONFIGURATIONS } from './memberTableColumnConfigurations';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { shouldSuggestTransferRequest } from './shouldSuggestTransferRequest';
import { getMemberPageUrl } from 'react-components/app/routing/routes';
import { useMobile } from '@act/utilities/mobile-context';

interface MembersTableProps {
  handlePageChange: (number) => void;
  handleRowsPerPageChange: (number) => void;
  handleSortChange: (orderyBy, orderDirection) => void;
}

const MembersTable = ({
  handlePageChange,
  handleRowsPerPageChange,
  handleSortChange,
}: MembersTableProps) => {
  const { isMobile } = useMobile();
  // Setting up dialog(s)
  const openTransferRequestDialog = useTransferRequestDialog();

  // Selecting relevant data from the store
  const guideMap = userAppSelectors.useSelectUsersMap();
  const paginatedMembers = membersAppSelectors.useSelectAllPaginatedMembers();
  const totalRecords = membersAppSelectors.useSelectPaginatedMemberCount();
  const status = useAppSelector(state => state.members.status);

  // Setting up state for sorting
  const [bounds, setBounds] = useState<{ size: number; start: number }>({
    size: 0,
    start: 0,
  });

  // Setting up navigation
  const navigate = useAppNavigation();

  // Setting up state for additional menu
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const [requestMember, setRequestMember] = useState<UserDto>(null);
  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const open = Boolean(additionalMenuAnchor);
  const openAdditionalMenu = (
    event: React.MouseEvent<HTMLElement>,
    member: UserDto
  ) => {
    setRequestMember(member);
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const closeAdditionalMenu = () => {
    setRequestMember(null);
    setAdditionalMenuAnchor(null);
  };

  useEffect(() => {
    function getBounds() {
      const stringifiedFilterConfig =
        window.localStorage.getItem('filterConfig');
      const parsedFilterConfig = JSON.parse(stringifiedFilterConfig);

      if (parsedFilterConfig) {
        setBounds({
          start: parsedFilterConfig['page-start'] || 0,
          size: parsedFilterConfig['page-size'] || 10,
        });
      }
    }

    if (paginatedMembers) {
      if (paginatedMembers.length === 0) {
        setBounds({
          start: 0,
          size: 0,
        });
      } else {
        getBounds();
      }
    }
  }, [paginatedMembers]);

  function onPaginationOrSort(parameters: PaginationOrSortParameters) {
    if (parameters.property && parameters.sortOrder) {
      handleSortChange(parameters.property, parameters.sortOrder);
    } else if (parameters.page !== undefined) {
      handlePageChange(parameters.page);
    } else if (parameters.rowsPerPage !== undefined) {
      handleRowsPerPageChange(parameters.rowsPerPage);
    }
  }

  return (
    <PaginatedTable
      status={status}
      inMemoryPagination={false}
      data={paginatedMembers.map(m => {
        const guide = guideMap[m.assignedGuideId];
        return {
          ...m,
          assignedGuide: guide?.name,
        };
      })}
      onRowClick={(member: UserDto) => {
        navigate.toMemberPage(member?.assignedGuideId, member?.id);
      }}
      columnConfig={MEMBER_TABLE_COLUMN_CONFIGURATIONS(isMobile)}
      page={bounds.start || 0}
      rowsPerPage={bounds.size || 10}
      rowsPerPageOptions={[5, 10, 25]}
      count={totalRecords || 0}
      menuConfiguration={{
        anchorEl: additionalMenuAnchor,
        open,
        openAdditionalMenu,
        menuItemConfigurations: [
          {
            label: 'Request member transfer',
            onClick: () => {
              void openTransferRequestDialog({ requestMember });
              closeAdditionalMenu();
            },
            isDisabled: !shouldSuggestTransferRequest(
              requestMember,
              actingUser
            ),
          },
          {
            label: 'Copy member link',
            onClick: () => {
              const memberPageUrl = getMemberPageUrl(
                window.location.pathname,
                requestMember.assignedGuideId,
                requestMember.id
              );
              void navigator.clipboard.writeText(memberPageUrl);
            },
            isDisabled: false,
          },
        ],
        onClose: closeAdditionalMenu,
      }}
      onPaginationOrSort={onPaginationOrSort}
    />
  );
};

export default MembersTable;
